import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import type { RouterState } from 'lib/routing/appRouting'
import { type SelfDescribingJson, setUserId } from '@snowplow/browser-tracker'

import type { AppStore } from '../../store'
import config from 'constants/config'
import { CART_SCHEMA, PAGE_VIEW_CONTEXT_SCHEMA } from 'constants/analytics'
import type { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import {
  buildInsuranceUpsellContext,
  createTourOfferItemContexts,
  experienceOfferEventsMap,
  getExperienceContext,
  getItemsContextForOffer,
  getItemsContextForCheckout,
  getPageOfferCategory,
  offerIdStateHasError,
  mapToSnowplowFilters,
  buildSearchContext,
  buildLuxPlusUpsellContext,
  getAnyItemContextForOffer,
  getOrderItemsContext,
  getOrderFromState,
  orderItemTransactionKeyMap,
  getItemsContextForCart,
  getItemsContextForCartSafe,
} from './helpers'
import { guestUserContexts } from './guestUserContext'
import createSearchContext from './helpers/createSearchContext'
import type {
  ExperienceEventDefinitionKey,
  ExperienceEventEntity,
  ExperienceOfferEventContext,
} from './helpers'
import { reportClientError } from 'services/errorReportingService'
import type {
  GAImpressionFieldObject,
  GAProductFieldObject,
  SnowplowImpressionData,
} from 'analytics/eventDefinitions'
import { getPageCategoryAndType } from './pages'
import { isNonNullable, nonNullable } from 'lib/array/arrayUtils'
import { getCartId, getPayPalPaymentType } from 'selectors/checkoutSelectors'
import { getAttractionAttribution, clearAttractionAttribution } from 'lib/attractions/attractionAttribution'
import { clearContentTagAttribution, getContentTagAttribution } from 'lib/contentTags/contentTagAttribution'
import paymentContext from './eventContexts/paymentContext'
import type { SearchEventData } from 'contexts/GlobalSearch/GlobalSearchTracking'
import { checkoutStateIsReady } from 'middlewares/analyticsMiddleware'
import type { TrackableProduct } from 'components/OfferList/OfferListEventsContext'
import { checkoutUrlPattern } from 'constants/url'
import { luxPlusContext, luxPlusContextOnPurchaseSuccess } from './eventContexts/luxPlusContext'
import { funnelRuleContext, funnelRuleConfigContexts } from './eventContexts/funnelOptimiserContext'
import { getGoogleClientId, getGoogleSessionId } from './helpers/googleAnalyticsCookies'
import { getEffectiveSearchType, mapSearchContextToSchema } from 'lib/search/searchUtils'
import getHotelCheckoutItem from './eventContexts/item/checkoutHotelContext'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'

import { isBedbankItem, isLEHotelItem } from 'lib/checkout/checkoutUtils'
import getHotelCartItem from './eventContexts/item/cartHotelContext'
import getBedbankHotelCartItem from './eventContexts/item/cartBedbankContext'
import {
  getBedbankRateContext,
  getBedbankRateItemContext,
  getHotelRateContext,
  getHotelRateItemContext,
  getPropertyRateContext,
  getPropertyRateItemContext,
  getRateListContext,
} from './eventContexts/item/hotelRoomRateContext'
import { trackClientEvent, registerSnowplowTrackHelpers } from 'analytics/trackClientEvent'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { getAvailableItems, getItems } from 'lib/userCart/multiCartSnapshotUtils'
import { loyaltyContext } from './eventContexts/loyaltyContext'
import uuidV4 from 'lib/string/uuidV4Utils'
import getMerchandiseCheckoutItem from 'analytics/snowplow/eventContexts/item/checkoutMerchandiseContext'
import { getOfferFlightContexts, getFlightContextsForCheckout, getFlightContextsForCheckoutItems, getOrderFlightContext } from './eventContexts/flightContext'
import { getDomainUserId } from './helpers/domainUserId'
import sanitizeItemContextMetadata from './eventContexts/item/sanitizeItemContextMetadata'
import { languageContext } from './eventContexts/languageContext'
import { getCruisePurchaseSuccessOptimizelyContexts } from './eventContexts/item/orderCruiseContext'
import type { SnowplowItemContextType } from './snowplowItemContextTypes'

export type { SnowplowItemContextType } from './snowplowItemContextTypes'
export type SnowplowContextType = 'item' | 'order' | 'checkout' | 'luxPlus' | 'loyalty' | 'cart'
export interface ItemContextMetadata {
  airline?: string
  originAirportCode?: string
  arrivalAirportCode?: string
  memberPrice?: number
  nonMemberPrice?: number
  upgradeOption?: 'original' | 'offered'
  /** Flights: whole-trip totals on the departing bound, or per leg. Amounts only compare within a basis. */
  priceBasis?: 'round_trip' | 'per_bound'
  /** Flights: fares behind a `carrier_deepest` row, so item count and fare count reconcile. */
  memberFareCount?: number
  /** Flights: which question the row answers. Filter on it — `carrier_deepest` is not the cheapest fare. */
  selection?: 'carrier_deepest' | 'cheapest_member' | 'cheapest_public'
}

export interface SnowplowEventOptions {
  itemsToSkip?: Array<SnowplowItemContextType>
  contextsToSkip?: Array<SnowplowContextType>
}

type ItemContextSource = 'order' | 'checkout' | 'offer' | 'cart'

export interface BaseSnowplowEvent {
  context?: Array<SelfDescribingJson> | null
  // Only use this if you want to explicitly determine where the context handler looks in redux to determine the items data
  // By default is is determined by looking at what page the user is on or what information is on the page which currently works for all but one case
  options?: SnowplowEventOptions
  itemContextSource?: ItemContextSource
  itemContextTypesToSkip?: Array<SnowplowItemContextType>
}
export interface SelfDescribingSnowlplowEvent extends BaseSnowplowEvent {
  event: SelfDescribingJson
}
export interface PageviewSnowlplowEvent extends BaseSnowplowEvent {
  title: string
}

interface offerPageCheckAvailabilityEventParams {
  offer: App.Offer | App.BedbankOffer
  roomRateId?: string
  includesFlights?: boolean
  packageOptionPrice?: number
}

export type SnowplowEvent = SelfDescribingSnowlplowEvent | PageviewSnowlplowEvent

let _store: AppStore | undefined

const cartEventBaseOptions: SnowplowEventOptions = {
  contextsToSkip: ['checkout', 'item', 'order', 'luxPlus', 'cart'],
}

export function registerCartTrackingStore(store: AppStore) {
  _store = store
}

/**
 * Event definitions
 */
export interface SnowplowClientEventParams extends BaseSnowplowEvent {
  subject: string
  action: string
  category: string
  type: 'interaction' | 'nonInteraction' | 'operational'
  context?: Array<SelfDescribingJson>
  options?: SnowplowEventOptions
  // 2 fields necessary if the client event is to make it to optimizely
  optimizelyEventId?: string
  optimizelyEventKey?: string
}

interface PromotionCodeContext {
  codeName: string
  headerText?: string
  bodyText?: string
  displayOptionKey?: string
  promoDisplayId?: string
}

interface PromotionContext {
  promotionType: 'banner' | 'prompt'
  promotionName: string
}

interface CalendarMonthImpressionEventParams {
  lastInteractedPriceMember?: number
  lastInteractedPriceNonMember?: number
  monthNumber: number
  year: number
  offer?: TrackableProduct
  roomRateId?: string
}

/**
 * If using clientEvent to send an event to optimizely, you must provide the optimizelyEventId and optimizelyEventKey.
 * If you require a value to be passed, eg. a click position, you must use the syntax `ab-value: n` where n
 * is the number required.
 */
export function clientEvent(params: SnowplowClientEventParams): SnowplowEvent {
  if (params.optimizelyEventId && params.optimizelyEventKey) {
    params.context = [
      ...params.context || [],
      comluxgroup.createOptimizelyEventContext_1_0_0({
        id: params.optimizelyEventId,
        key: params.optimizelyEventKey,
      })]
  }

  return {
    context: params.context,
    options: {
      itemsToSkip: params.options?.itemsToSkip || [],
      contextsToSkip: params.options?.contextsToSkip || [],
    },
    event: comluxgroup.createClientEvent_1_0_0({
      subject: params.subject,
      action: params.action,
      type: params.type,
      category: params.category,
    }),
  }
}

export function impressionEvent(
  obj: GAImpressionFieldObject | GAProductFieldObject,
  listId: string,
  listName: string,
  currency: string,
  schema: string,
  listSource?: string,
): SnowplowEvent {
  const data: SnowplowImpressionData = {
    id: obj.id,
    name: obj.name,
    productType: obj.category,
    position: obj.position,
    currency,
    price: obj.price,
    listId,
    listName,
    listSource,
  }
  return {
    event: {
      schema,
      data,
    },
  }
}

export function luxPlusUpsellEvent(
  notSuitable: string,
  yes: string,
  whyNotSuitable?: string,
  isLuxPlusOffered?: string,
  declineReason?: string,
): SnowplowEvent {
  return {
    context: buildLuxPlusUpsellContext(
      notSuitable,
      yes,
      whyNotSuitable,
      isLuxPlusOffered,
      declineReason,
      _store ? _store.getState().auth.account.agentId : undefined,
    ),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'luxplus_upsell_reminder',
      type: 'interaction',
      action: 'submit',
      category: 'luxplus',
    }),
  }
}

export function spoofDisableLuxPlusConfirmedEvent(): void {
  trackClientEvent({
    subject: 'spoof_luxplus_for_customer',
    action: 'disabled',
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofDisableLuxPlusReEnabledEvent(): void {
  trackClientEvent({
    subject: 'spoof_luxplus_for_customer',
    action: 're_enabled',
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadImpressionEvent(): void {
  trackClientEvent({
    subject: 'spoof_launchpad',
    action: 'impression',
    category: 'spoof',
    type: 'nonInteraction',
  })
}

export function spoofLaunchpadExpandedEvent(): void {
  trackClientEvent({
    subject: 'spoof_launchpad',
    action: 'expanded',
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadCollapsedEvent(): void {
  trackClientEvent({
    subject: 'spoof_launchpad',
    action: 'collapsed',
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadTabClickedEvent(tab: 'activity' | 'chat_trips' | 'purchases' | 'recommendations' | 'vouchers' | 'guide'): void {
  trackClientEvent({
    subject: 'spoof_launchpad_tab',
    action: tab,
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadActivityActionEvent(
  rowType: 'search' | 'offer_view' | 'cart',
  action: 'open' | 'restore_cart',
): void {
  trackClientEvent({
    subject: 'spoof_launchpad_activity_row',
    action: `${rowType}_${action}`,
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadActivitySectionClickedEvent(section: 'search' | 'offer_view' | 'cart'): void {
  trackClientEvent({
    subject: 'spoof_launchpad_activity_section',
    action: section,
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadActivityRowOpenedEvent(rowType: 'search' | 'offer_view' | 'cart' | 'cart_offer'): void {
  trackClientEvent({
    subject: 'spoof_launchpad_activity_row_opened',
    action: rowType,
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadRecommendationOpenedEvent(): void {
  trackClientEvent({
    subject: 'spoof_launchpad_recommendation_row',
    action: 'open',
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadOrderActionEvent(action: 'my_escapes' | 'copy_admin_link'): void {
  trackClientEvent({
    subject: 'spoof_launchpad_order_row',
    action,
    category: 'spoof',
    type: 'interaction',
  })
}

export function spoofLaunchpadChatActionEvent(): void {
  trackClientEvent({
    subject: 'spoof_launchpad_chat_row',
    action: 'open_chat',
    category: 'spoof',
    type: 'interaction',
  })
}

export function agentInsuranceUpsellEvent(
  notSuitable: string,
  yes: string,
  no: string,
  whyNotSuitable?: string,
  isCovermore?: string,
  isOffered?: string,
  declineReason?: string,
): SnowplowEvent {
  return {
    context: buildInsuranceUpsellContext(
      notSuitable,
      yes,
      no,
      whyNotSuitable,
      isCovermore,
      isOffered,
      declineReason,
      _store ? _store.getState().auth.account.agentId : undefined,
    ),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'insurance_upsell_reminder',
      type: 'interaction',
      action: 'submit',
      category: 'checkout',
    }),
  }
}

export function experienceEvent(
  eventDefinitionKey: ExperienceEventDefinitionKey,
  geo: App.GeoState, eventsContexts?: Array<ExperienceEventEntity>,
  subjectOverride?: string,
  source?: string,
): SnowplowEvent {
  const event = experienceOfferEventsMap[eventDefinitionKey]
  const defaultEventContext: ExperienceOfferEventContext = {
    type: 'impression',
    data: {
      geo,
      listProps: {
        listName: source ?? 'unknown',
      },
    },
  }
  const eventsContextsWithGeo = eventsContexts?.map<ExperienceOfferEventContext>((eventContext) => {
    // to handle with TS
    if (eventContext.type === 'impression') {
      return {
        ...eventContext,
        data: {
          ...eventContext.data,
          geo,
        },
      }
    }

    return {
      ...eventContext,
      data: {
        ...eventContext.data,
        geo,
      },
    }
  }) ?? []
  return {
    context: getExperienceContext([defaultEventContext, ...eventsContextsWithGeo]),
    event: comluxgroup.createClientEvent_1_0_0({
      ...event,
      ...(subjectOverride ? { subject: subjectOverride } : {}),
      category: 'experiences_offer',
    }),
  }
}

export type AuthState = 'started' | 'succeeded' | 'failed'

export function offerPageHotLeadEvent(
  offer: TrackableProduct,
  offerPageState: OfferPageState,
): SnowplowEvent | undefined {
  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }

  try {
    const metadata = JSON.stringify({
      lastInteractedPriceMember: offerPageState.lastInteractedCalendarDay?.memberPricing?.price,
      lastInteractedPriceNonMember: offerPageState.lastInteractedCalendarDay?.pricing.price,
    })
    return {
      context: getAnyItemContextForOffer({ state, offer, offerPageState, metadata }),
      options: { contextsToSkip: ['item'] },
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'hot_lead',
        type: 'interaction',
        action: 'sent',
        category: 'offer_page',
      }),
    }
  } catch(e) {
    reportClientError(e)
  }
}

export function offerPageCalendarImpressionEvent(params: CalendarMonthImpressionEventParams): SnowplowEvent | undefined {
  const { lastInteractedPriceMember, lastInteractedPriceNonMember, monthNumber, year, offer, roomRateId } = params
  const event = comluxgroup.createClientEvent_1_0_0({
    subject: 'calendar-month-impression',
    action: `view_${monthNumber}_${year}`,
    category: 'offer_funnel',
    type: 'nonInteraction',
  })

  const state: App.State | undefined = _store?.getState()
  if (!state || !offer) {
    return { event }
  }

  try {
    const metadata = lastInteractedPriceNonMember === undefined ? undefined : sanitizeItemContextMetadata({
      lastInteractedPriceMember,
      lastInteractedPriceNonMember,
    })

    return {
      context: getAnyItemContextForOffer({ state, offer, roomRateId, metadata }),
      options: { contextsToSkip: ['item'] },
      event,
    }
  } catch(e) {
    reportClientError(e)
  }
}

export function offerPageCheckAvailabilityEvent(params: offerPageCheckAvailabilityEventParams): SnowplowEvent | undefined {
  const { offer, roomRateId, includesFlights, packageOptionPrice } = params

  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }

  try {
    return {
      context: getAnyItemContextForOffer({
        state,
        offer,
        price: packageOptionPrice,
        roomRateId,
        metadata: JSON.stringify({
          flightOptionSelected: (includesFlights ?? false).toString(),
        }),
      }),
      options: { contextsToSkip: ['item'] },
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'check_availability',
        type: 'interaction',
        action: 'sent',
        category: 'offer_page',
      }),
    }
  } catch(e) {
    reportClientError(e)
  }
}

export function offerPageSoldOutEvent(
  offer: App.Offer | App.BedbankOffer,
  offerPageState?: OfferPageState,
): SnowplowEvent | undefined {
  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }

  try {
    return {
      context: getAnyItemContextForOffer({ state, offer, offerPageState }),
      options: { contextsToSkip: ['item'] },
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'availability_result',
        type: 'nonInteraction',
        action: 'sold_out',
        category: 'offer_page',
      }),
    }
  } catch(e) {
    reportClientError(e)
  }
}

export function offerPageGuestSelectionEvent(
  offer: App.Offer | App.BedbankOffer,
  offerPageState?: OfferPageState,
): SnowplowEvent | undefined {
  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }

  try {
    return {
      context: getAnyItemContextForOffer({ state, offer, offerPageState }),
      options: { contextsToSkip: ['item'] },
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'guest_selection',
        type: 'interaction',
        action: 'sent',
        category: 'offer_page',
      }),
    }
  } catch(e) {
    reportClientError(e)
  }
}

export function offerPageDurationChangeEvent(
  offer: App.Offer | App.BedbankOffer,
  offerPageState?: OfferPageState,
): SnowplowEvent | undefined {
  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }

  try {
    return {
      context: getAnyItemContextForOffer({ state, offer, offerPageState }),
      options: { contextsToSkip: ['item'] },
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'duration_selection',
        type: 'interaction',
        action: 'changed',
        category: 'offer_page',
      }),
    }
  } catch(e) {
    reportClientError(e)
  }
}

interface SearchContextsParams {
  searchContext: comluxgroup.SearchContext_1_1_2
  sortContext?: comluxgroup.Sort_1_0_0
  filterContexts?: Array<comluxgroup.Filter_1_0_0>
  searchSessionId?: string
}

type SearchResultsEventParams = SearchContextsParams & Pick<comluxgroup.SearchResults_1_0_1, 'numberOfResults' | 'status' | 'durationMs'>

function buildSearchEventContexts({ searchContext, filterContexts = [], sortContext, searchSessionId }: SearchContextsParams): Array<SelfDescribingJson> {
  const searchContextData = createSearchContext({
    ...searchContext,
  })

  const filterContextList = filterContexts.map(
    filterContext => comluxgroup.createFilter_1_0_0({
      ...filterContext,
    })) ?? []

  const sortContextData = sortContext ? comluxgroup.createSort_1_0_0({
    ...sortContext,
  }) : undefined

  const searchSessionContext = comluxgroup.createSearchSession_1_0_0({
    searchSessionId: searchSessionId ?? uuidV4(),
  })

  return [
    searchContextData,
    searchSessionContext,
    ...(sortContextData ? [sortContextData] : []),
    ...filterContextList,
  ]
}

export function searchEventWithContext({ searchEvent, searchContext, filterContexts = [], sortContext, searchSessionId }: SearchContextsParams & {
  searchEvent: comluxgroup.Search_3_0_0
}): SnowplowEvent {
  const { type } = searchEvent

  return {
    context: buildSearchEventContexts({
      searchContext,
      filterContexts,
      sortContext,
      searchSessionId,
    }),
    event: comluxgroup.createSearch_3_0_0({
      type,
    }),
  }
}

export function searchResultsEventWithContext({
  numberOfResults,
  searchContext,
  filterContexts = [],
  sortContext,
  searchSessionId,
  status,
  durationMs,
}: SearchResultsEventParams): SnowplowEvent {
  return {
    context: buildSearchEventContexts({
      searchContext,
      filterContexts,
      sortContext,
      searchSessionId,
    }),
    event: comluxgroup.createSearchResults_1_0_1({
      numberOfResults,
      status: status ?? null,
      durationMs: durationMs ?? null,
    }),
  }
}

export function impressionEventWithContext(
  position: number,
  list: string,
  listInstance: string,
  offer: TrackableProduct,
  additionalData: {
    productAvailable?: boolean
    price?: number
    memberPrice?: number
    duration?: number
    searchContext?: App.SearchContext
    roomRateId?: string
    filterContexts?: Array<comluxgroup.Filter_1_0_0>
    sortContext?: comluxgroup.Sort_1_0_0
  } = {},
): SnowplowEvent | undefined {
  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }
  const {
    productAvailable,
    price,
    memberPrice,
    duration,
    searchContext,
    roomRateId,
    filterContexts = [],
    sortContext,
  } = additionalData

  const itemContext = getAnyItemContextForOffer({
    state,
    offer,
    price,
    memberPrice,
    duration,
    productAvailable,
    addPricesToMetaData: false, // to be enabled once item schema is updated
    roomRateId,
  }) ?? []
  const mappedSearchContext = searchContext ? mapSearchContextToSchema(searchContext) : undefined

  const snowplowSearchContext = mappedSearchContext ? [createSearchContext({
    ...mappedSearchContext,
  })] : []

  const sortContextArray = sortContext ? [comluxgroup.createSort_1_0_0({
    ...sortContext,
  })] : []

  const filterContextArray = filterContexts.map(
    filterContext => comluxgroup.createFilter_1_0_0({
      ...filterContext,
    }))

  const flightContexts = getOfferFlightContexts(offer)

  return {
    context: [
      ...itemContext,
      ...flightContexts,
      ...snowplowSearchContext,
      ...filterContextArray,
      ...sortContextArray,
      comluxgroup.createListItem_1_0_0({
        listInstance,
        listName: list,
        itemPosition: position,
      }),
    ],
    event: comluxgroup.createProductImpression_1_0_3({ productAvailable }),
    options: {
      contextsToSkip: ['item'],
    },
  }
}

export function carouselImageImpressionEvent(image: App.Image, position: number, list: string, listInstance: string, offer: TrackableProduct, packageId?: string): SnowplowEvent | undefined {
  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }

  const imageContext = [comluxgroup.createImage_1_0_0({
    imageId: image.id ?? '',
    imageTags: image.tags ?? [],
    imageWidth: image.width ?? 0,
    imageHeight: image.height ?? 0,
  })]

  const listContext = [comluxgroup.createListItem_1_0_0({
    listInstance,
    listName: list,
    itemPosition: position,
  })]

  const packageContext = packageId ? [comluxgroup.createPackageOption_1_0_1({ id: packageId, inclusions: [], cancellationPolicy: {} })] : []

  const itemContext = getAnyItemContextForOffer({
    state,
    offer,
  }) ?? []

  return {
    context: [...imageContext, ...listContext, ...itemContext, ...packageContext],
    event: comluxgroup.createCarouselImageImpression_1_0_0({}),
    options: {
      contextsToSkip: ['item'],
    },
  }
}

interface BaseRateContextData {
  occupancy: Array<App.Occupants>
  checkIn?: string
  checkOut?: string
  roomListId: string
  roomPosition: number
  rateListId: string
  ratePosition: number
}

interface BedbankRateContextData extends BaseRateContextData {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  rate: App.BedbankRate
  price: number
  value: number
  checkIn: string
  checkOut: string
  currency: string
  isFlightBundle: boolean
}

interface PropertyRateContextData extends BaseRateContextData {
  rate: App.EnrichedPropertyRate
  displayPrice?: number
  displayMemberPrice?: number
  displayNonMemberPrice?: number
  displayValue?: number
}

interface HotelRateContextData {
  offer: App.HotelOffer
  packageView: App.OfferPackageOptionView
  displayPrice: number
  checkIn?: string
  checkOut?: string
  occupancy: Array<App.Occupants>
  currency: string
  displayMemberPrice?: number
  displayNonMemberPrice?: number
  displayValue?: number
}

export function bedbankRateImpressionEventWithContext(
  contextData: BedbankRateContextData,
): SnowplowEvent | undefined {
  const {
    ratePosition,
    rateListId,
    roomPosition,
    roomListId,
    offer,
    rate,
    price,
    value,
    checkIn,
    checkOut,
    occupancy,
    currency,
    isFlightBundle,
  } = contextData

  const itemContext = getBedbankRateItemContext(
    offer,
    rate,
    price,
    value,
    checkIn,
    checkOut,
    occupancy,
    currency,
  )

  const listContext = getRateListContext(
    'bedbank',
    ratePosition,
    undefined,
    rateListId,
    roomPosition,
    roomListId,
  )

  const rateContext = getBedbankRateContext(rate, isFlightBundle)

  return {
    context: [
      ...itemContext,
      ...listContext,
      ...rateContext,
    ],
    event: comluxgroup.createPackageOptionImpression_1_0_0({}),
    options: {
      contextsToSkip: ['item'], // exclude default item context since we have explicit item context
    },
  }
}

export function propertyRateImpressionEventWithContext(
  contextData: PropertyRateContextData,
): SnowplowEvent | undefined {
  const {
    occupancy,
    checkIn,
    checkOut,
    roomListId,
    roomPosition,
    rateListId,
    ratePosition,
    rate,
    displayPrice,
    displayMemberPrice,
    displayNonMemberPrice,
    displayValue,
  } = contextData

  const itemContext = getPropertyRateItemContext(
    occupancy,
    checkIn,
    checkOut,
    rate,
    displayPrice,
    displayMemberPrice,
    displayNonMemberPrice,
    displayValue,
  )

  const listContext = getRateListContext(
    'property',
    ratePosition,
    rate.orderReason,
    rateListId,
    roomPosition,
    roomListId,
  )

  const rateContext = getPropertyRateContext(rate)

  return {
    context: [
      ...itemContext,
      ...listContext,
      ...rateContext,
    ],
    event: comluxgroup.createPackageOptionImpression_1_0_0({}),
    options: {
      // Exclude default item context because we have an explicit item context.
      contextsToSkip: ['item'],
    },
  }
}

export function hotelRateImpressionEventWithContext(
  contextData: HotelRateContextData,
): SnowplowEvent {
  const {
    offer,
    packageView,
    displayPrice,
    checkIn,
    checkOut,
    occupancy,
    currency,
    displayMemberPrice,
    displayNonMemberPrice,
    displayValue,
  } = contextData

  return {
    context: [
      ...getHotelRateItemContext(offer, packageView, displayPrice, checkIn, checkOut, occupancy, currency, displayMemberPrice, displayNonMemberPrice, displayValue),
      ...getHotelRateContext(packageView),
    ],
    event: comluxgroup.createPackageOptionImpression_1_0_0({}),
    options: {
      contextsToSkip: ['item'],
    },
  }
}

export function productClickEventWithContext(
  position: number,
  list: string,
  listInstance: string,
  offer: TrackableProduct,
  additionalData: {
    productAvailable?: boolean
    price?: number
    memberPrice?: number
    duration?: number
    searchContext?: App.SearchContext
    filterContexts?: Array<comluxgroup.Filter_1_0_0>
    sortContext?: comluxgroup.Sort_1_0_0
    isRightClick?: boolean
  } = {},
): SnowplowEvent | undefined {
  const state: App.State | undefined = _store?.getState()
  if (!state) {
    return
  }
  const {
    productAvailable,
    price,
    memberPrice,
    duration,
    searchContext,
    filterContexts = [],
    sortContext,
    isRightClick = false,
  } = additionalData

  const itemContext = getAnyItemContextForOffer({
    state,
    offer,
    price,
    memberPrice,
    duration,
    productAvailable,
    addPricesToMetaData: false, // To enable once the item schema is updated
  }) ?? []
  const mappedSearchContext = searchContext ? mapSearchContextToSchema(searchContext) : undefined

  const snowplowSearchContext = mappedSearchContext ? [createSearchContext({
    ...mappedSearchContext,
  })] : []

  const filterContextArray = filterContexts.map(
    filterContext => comluxgroup.createFilter_1_0_0({
      ...filterContext,
    }))

  const sortContextArray = sortContext ? [comluxgroup.createSort_1_0_0({
    ...sortContext,
  })] : []

  const flightContexts = getOfferFlightContexts(offer)

  return {
    context: [
      ...itemContext,
      ...flightContexts,
      ...snowplowSearchContext,
      ...filterContextArray,
      ...sortContextArray,
      comluxgroup.createListItem_1_0_0({
        listInstance,
        listName: list,
        itemPosition: position,
      })],
    event: isRightClick
      ? comluxgroup.createProductRightClick_1_0_0({})
      : comluxgroup.createProductClick_1_0_2({}),
    options: {
      contextsToSkip: ['item'],
    },
  }
}

export function searchEvent(subject: string, category: string, data: SearchEventData): SnowplowEvent {
  const { searchItem, checkIn, checkOut, vertical, isFlexibleDates, occupancies, filters } = data
  const baseContext = buildSearchContext()

  const formattedOccupancies = (occupancies ?? []).map(
    occupants => ({ numberOfChildren: (occupants?.infants ?? 0) + (occupants?.children ?? 0), numberOfAdults: occupants.adults }))

  return {
    context: [comluxgroup.createSearch_1_0_2({
      ...baseContext,
      searchType: getEffectiveSearchType(searchItem) ?? '',
      verticals: [vertical],
      searchParams: {
        ...baseContext.searchParams,
        checkIn: checkIn ?? null,
        checkOut: checkOut ?? null,
        occupancies: formattedOccupancies,
        isFlexibleDates: Boolean(isFlexibleDates),
        searchText: searchItem?.format?.mainText ?? null,
        filters: filters ? mapToSnowplowFilters(filters) : {},
      },
    })],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'clicked',
      subject,
      type: 'interaction',
      category,
    }),
  }
}

function getConfirmationPageOrderId(routerState: RouterState): string | undefined {
  return routerState?.location?.query?.orderId
}

/**
 *
 * Use this function to add any global context to Snowplow events that are async
 * The function will be called right before each event is sent and awaited
 */
export async function asyncBaseContext(itemContextSource?: ItemContextSource, options: SnowplowEventOptions = {}): Promise<Array<SelfDescribingJson<Record<string, unknown>>> | null | undefined> {
  if (!_store) {
    return []
  }

  const state: App.State = _store.getState()
  const routerState: RouterState = state.router

  // See if the page is part of the mapped analytics pages
  const pageData = getPageCategoryAndType(routerState.location.pathname)

  // Otherwise, see if it is an offer page
  const { category: pageCategory, hotelOrTourOffer, otherOffers, merchandiseOffer } = await getPageOfferCategory(_store.getState())

  if (!pageData.category && !pageData.type && pageCategory) {
    pageData.category = pageCategory
    pageData.type = 'offer'
  }

  const context: Array<SelfDescribingJson<Record<string, unknown>>> = []
  const promises: Array<Promise<Array<SelfDescribingJson> | undefined>> = []

  // Get items context just when it's an offer or checkout page
  const currentPath = state.routeHistory.currentPath
  const pathParams = state.routeHistory.currentPathParams as { orderId?: string, offerId?: string, id?: string, step?: string }
  const isCheckoutPage = checkoutUrlPattern.test(currentPath)
  const isCartPage = pageData.category === 'cart'
  const offerId = pathParams.offerId || pathParams.id
  const isOfferPage = !!offerId
  const isOrderPage = !!(pathParams.orderId || getConfirmationPageOrderId(state.router))
  const { itemsToSkip, contextsToSkip } = options

  const contextFunctionMap: Record<ItemContextSource, () => void | number> = {
    checkout: () => {
      promises.push(checkoutStateIsReady().then(() => getItemsContextForCheckout(_store!.getState(), itemsToSkip)))
      promises.push(checkoutStateIsReady().then(() => getFlightContextsForCheckout(_store!.getState())))
    },
    order: () => {
      const order = getOrderFromState(state)
      if (order) {
        promises.push(getOrderItemsContext(state, order))
        promises.push(getOrderFlightContext(state, order))
      }
    },
    offer: () => {
      const itemsContext = getItemsContextForOffer(state, { hotelOrTourOffer, otherOffers, merchandiseOffer })
      if (itemsContext) context.push(...itemsContext)
      if (offerId && offerIdStateHasError(state, offerId)) {
        pageData.type = 'error'
      }
    },
    cart: () => {
      const cartMachineActor = getCartMachineActor()
      if (cartMachineActor) {
        const snapshot = cartMachineActor.getSnapshot()
        const items = getItems(snapshot)
        promises.push(getItemsContextForCartSafe(_store!.getState(), items, itemsToSkip, true))
      }
    },
  }

  if (itemContextSource) {
    contextFunctionMap[itemContextSource]?.()
  } else {
    if (isCheckoutPage && !contextsToSkip?.includes('checkout')) {
      contextFunctionMap.checkout()
    } else if (isOrderPage && !contextsToSkip?.includes('order')) {
      contextFunctionMap.order()
    } else if (isOfferPage && !contextsToSkip?.includes('item')) {
      contextFunctionMap.offer()
    } else if (isCartPage && !contextsToSkip?.includes('cart')) {
      contextFunctionMap.cart()
    }
  }

  if (!contextsToSkip?.includes('luxPlus')) {
    context.push(luxPlusContext(state))
  }

  if (!contextsToSkip?.includes('loyalty')) {
    context.push(loyaltyContext(state.loyalty.account.data))
  }

  const promiseContexts = await Promise.all(promises)
  context.push(...nonNullable(promiseContexts.flat()))
  context.push(
    comluxgroup.createPage_1_0_2({
      type: pageData.type,
      category: pageData.category,
    }),
  )
  return context
}

export function baseContext(event?: { eventSchema: string }): Array<SelfDescribingJson> {
  if (!_store) {
    return []
  }

  const state = _store.getState()

  const authState: App.AuthState = state.auth
  const geoState: App.GeoState = state.geo

  const optimizelyExperiments = state.optimizely.optimizelyExperiments
  const chertoExperimentEntries = Object.entries(state.cherto?.chertoExperiments ?? {}).map(([key, decisions]) => [
    `cherto_${key}`,
    decisions,
  ] as const)
  const triggeredExperiments = [
    ...Object.entries(optimizelyExperiments),
    ...chertoExperimentEntries,
  ]
    .filter(([_, decisions]) => decisions?.triggered && decisions?.variationKey)
    .map(([key, decisions]) => ({
      key: `exp_${key}`,
      value: decisions?.variationKey || '',
    }))
  const labFlags = Object.entries(state.system.features.labFlags)
    .filter(([_, decisions]) => decisions?.campaignId && decisions?.variant)
    .map(([key, decisions]) => ({
      key: `lab_${key}`,
      value: `${decisions?.campaignId}-${decisions?.variant}`,
    }))

  // Use resolvedTrackingId from Redux state instead of re-parsing document.cookie
  // on every event. The resolvedTrackingId is kept up to date by SSR and login rebucketing.
  const seedId = authState.resolvedTrackingId ?? getDomainUserId()
  const isSeedIdStitched = !!authState.resolvedTrackingId && authState.resolvedTrackingId !== authState.domainUserId

  const context = [
    comluxgroup.createBrand_1_0_0({
      brand: config.BRAND,
    }),
    comluxgroup.createRegion_1_0_0({
      region: geoState.currentRegionCode,
    }),
    languageContext(),
    comluxgroup.createExperimentContext_1_0_0({
      flags: [...triggeredExperiments, ...labFlags],
    }),
    comluxgroup.createUtms_1_0_0({
      source: state.utm.source,
      medium: state.utm.medium,
      campaign: state.utm.campaign,
      term: state.utm.term,
      content: state.utm.content,
    }),
    ...(seedId ? [
      comluxgroup.createVisitor_1_0_0({
        seedId,
        isSeedIdStitched,
      }),
    ] : []),
  ]

  // Add funnel optimiser contexts
  const funnelRule = funnelRuleContext(state)
  if (funnelRule) {
    context.push(funnelRule)
  }
  const funnelConfigs = funnelRuleConfigContexts(state)
  if (funnelConfigs.length > 0) {
    context.push(...funnelConfigs)
  }

  const isFlightResultsView = event?.eventSchema === 'iglu:com.luxgroup/flight_search_results_viewed/jsonschema/1-0-0'
  if (!isFlightResultsView && authState.account.memberId && authState.account.email) {
    setUserId(authState.account.memberId)
    context.push(comluxgroup.createUser_1_0_1({
      id: authState.account.memberId,
      email: authState.account.email,
      first_name: authState.account.givenName,
      is_spoofed: authState.account.isSpoofed,
      account_type: authState.account.accountType,
    }))
    context.push(comluxgroup.createUserPurchaseCount_1_0_0({
      number_of_purchases: authState.account.numberOfPurchases || 0,
    }))
  } else if (!isFlightResultsView) {
    context.push(...guestUserContexts())
  }

  if (authState.account.isSpoofed && authState.account.spoofedBy?.id) {
    context.push(comluxgroup.createSpoofingAgent_1_0_0({
      userId: authState.account.spoofedBy.id,
    }))
  }

  context.push(comluxgroup.createGoogleAnalytics_1_0_0({
    googleClientId: getGoogleClientId(),
    googleSessionId: getGoogleSessionId(),
  }))
  return context
}

registerSnowplowTrackHelpers({
  asyncBaseContext,
  clientEvent,
})

export function signupSuccessEvent(method: App.SignUpAuthSource): SnowplowEvent {
  return {
    event: comluxgroup.createSignupSuccess_1_0_0({
      method,
    }),
  }
}

export function signinSuccessEvent(method: App.AuthSource): SnowplowEvent {
  return {
    event: comluxgroup.createSigninSuccess_1_0_0({
      method,
    }),
  }
}

export function selectPackageUpgradeOptionToCart(
  offer: App.Tours.TourV2Offer,
  option: string,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  tourItems: Array<App.Checkout.TourV2Item>,
): SnowplowEvent {
  return {
    context: createTourOfferItemContexts(offer, regionCode, currency, tourItems),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: `tour_upgrade_${option.toLowerCase()}`,
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function trackPageView(pathTemplate: string, pathParamsObject: any): SnowplowEvent {
  return {
    title: document.title,
    context: [{
      schema: PAGE_VIEW_CONTEXT_SCHEMA,
      data: {
        pathTemplate,
        pathParams: JSON.stringify(pathParamsObject),
      },
    }],
  }
}

export async function purchaseSuccess(order: App.Order, isMultiCart?: boolean): Promise<SnowplowEvent | undefined> {
  try {
    const state = _store?.getState()
    if (!state) { throw new Error('No redux registered for tracking purchase success snowplow events') }
    const cartId = getCartId(state)
    const paypalPaymentType = getPayPalPaymentType(state)
    const orderItemContext = await getOrderItemsContext(state, order, isMultiCart === true ? true : undefined)
    const luxPlusContext = luxPlusContextOnPurchaseSuccess(state, order)
    const orderItemMap = orderItemTransactionKeyMap(order)
    const orderToCartItemsContexts = state.checkout.cart.items
      .map(item => {
        const orderItemId = orderItemMap.get(item.transactionKey)?.id
        if (!orderItemId) return
        return comluxgroup.createCartOrderLink_1_0_0({
          cartItemId: item.itemId,
          orderItemId,
        })
      })
      .filter(isNonNullable)
    const orderFlightContext = await getOrderFlightContext(state, order, state.checkout.cart.items)

    const hasExperienceItems = order.experienceItems?.length > 0
    const cruisePurchaseOptimizelyContexts = getCruisePurchaseSuccessOptimizelyContexts(order)
    const rawAttribution = hasExperienceItems ? getAttractionAttribution() : null
    const attractionAttribution = typeof rawAttribution?.id === 'string' && typeof rawAttribution?.name === 'string' ? rawAttribution : null
    const attractionContext = attractionAttribution
      ? comluxgroup.createProductClickContext_1_0_1({
          id: attractionAttribution.id,
          name: attractionAttribution.name,
          productType: 'attraction_attribution',
        })
      : null
    if (attractionContext) clearAttractionAttribution()
    const rawContentTagAttribution = hasExperienceItems ? getContentTagAttribution() : null
    const contentTagAttribution = typeof rawContentTagAttribution?.id === 'string' ? rawContentTagAttribution : null
    const contentTagContext = contentTagAttribution
      ? comluxgroup.createProductClickContext_1_0_1({
          id: contentTagAttribution.id,
          productType: 'content_tag_attribution',
        })
      : null
    if (contentTagContext) clearContentTagAttribution()

    return {
      // Skip the built in item context for order and checkout as we are generating our own
      options: {
        contextsToSkip: ['order', 'checkout', 'luxPlus'],
      },
      context: [
        comluxgroup.createCart_1_0_0({
          cartId,
        }),
        ...paymentContext(order.payments, paypalPaymentType),
        ...orderItemContext,
        luxPlusContext,
        ...orderToCartItemsContexts,
        ...orderFlightContext,
        ...(attractionContext ? [attractionContext] : []),
        ...(contentTagContext ? [contentTagContext] : []),
        ...cruisePurchaseOptimizelyContexts,
      ],
      event: comluxgroup.createPurchaseSuccess_1_0_0({}),
    }
  } catch(e) {
    reportClientError(e)
  }
}

interface GuestCheckoutPurchaseSuccess {
  orderId: string
  amount: number
  currency: string
  numberOfItems: number
  payments: Array<App.OrderPayment>
}

/**
 * Guest checkout cannot read the authenticated order endpoint after payment.
 * Build the purchase contexts from the checkout snapshot while it is still
 * available, then associate those items with the completed public order.
 */
export async function guestCheckoutPurchaseSuccess({
  orderId,
  amount,
  currency,
  numberOfItems,
  payments,
}: GuestCheckoutPurchaseSuccess): Promise<SnowplowEvent | undefined> {
  try {
    const state = _store?.getState()
    if (!state) { throw new Error('No redux registered for tracking guest checkout purchase success snowplow events') }

    const cartId = getCartId(state)
    const paypalPaymentType = getPayPalPaymentType(state)
    // Guest checkout has no order endpoint; its cart retains the selected rate and margin.
    const cartItemContexts = await getItemsContextForCart(state, state.checkout.cart.items)
    const itemContexts = cartItemContexts.length > 0
      ? cartItemContexts
      : await getItemsContextForCartSafe(state, state.checkout.cart.items)
    const orderItemContexts = itemContexts.map(itemContext => ({
      ...itemContext,
      data: {
        ...itemContext.data,
        orderId,
      },
    }))

    return {
      options: {
        contextsToSkip: ['order', 'checkout', 'luxPlus', 'loyalty'],
      },
      context: [
        comluxgroup.createCart_1_0_0({ cartId }),
        comluxgroup.createOrder_1_0_0({
          orderId,
          amount,
          currency,
          numberOfItems,
          paymentOption: 'full',
        }),
        ...paymentContext(payments, paypalPaymentType),
        ...orderItemContexts,
      ],
      event: comluxgroup.createPurchaseSuccess_1_0_0({}),
    }
  } catch(e) {
    reportClientError(e)
  }
}

type AddToCartContextData = {
  ratePosition: number
  rateListId: string
  roomPosition: number
  roomListId: string
  occupancy: Array<App.Occupants>
  /**
   * `true` for add-to-cart flows, which stay on the offer page: there is no checkout state to wait
   * on, so the event carries its own item context and fires immediately (HP-2730). `false` for
   * book-now flows, which navigate to checkout and resolve the richer checkout item context there,
   * exactly as they did before HP-2730.
   */
  staysOnPage: boolean
} & ({
  type: 'bedbank'
  offer: App.BedbankOffer | App.BedbankOfferSummary
  rate: App.BedbankRate
  isFlightBundle: boolean
  price: number
  value: number
  checkIn: string
  checkOut: string
  currency: string
} | {
  type: 'property'
  rate: App.EnrichedPropertyRate
  checkIn: string
  checkOut: string
  isLuxPlusMember: boolean
  displayMemberPrice?: number
  displayNonMemberPrice?: number
  displayValue?: number
})

function buildAddToCartEvent(itemContexts: Array<SelfDescribingJson>): SnowplowEvent {
  if (!_store) {
    throw new Error('No redux registered for tracking cart snowplow events')
  }
  const state = _store.getState()
  const cartId = getCartId(state)

  return {
    itemContextSource: 'checkout',
    context: [
      {
        schema: CART_SCHEMA,
        data: { cartId },
      },
      ...itemContexts,
    ],
    event: comluxgroup.createAddToCart_1_0_0({}),
  }
}

// Item and rate contexts are known synchronously here, so fire immediately rather than via
// `buildAddToCartEvent`'s `itemContextSource: 'checkout'`, which wouldn't resolve until the user
// reaches a checkout URL (HP-2730). Every default item-context source is skipped, so callers must
// pass an explicit item context; without one the event carries no offer, product or price and no
// longer lands in the add-to-cart reporting models. 'luxPlus' is kept, unlike
// `addToCartWithContext`, because these events have always carried it.
function buildAddToCartEventWithContext(itemContexts: Array<SelfDescribingJson>): SnowplowEvent {
  if (!_store) {
    throw new Error('No redux registered for tracking cart snowplow events')
  }
  const state = _store.getState()
  const cartId = getCartId(state)

  return {
    options: {
      contextsToSkip: ['checkout', 'item', 'order', 'cart'],
    },
    context: [
      {
        schema: CART_SCHEMA,
        data: { cartId },
      },
      ...itemContexts,
    ],
    event: comluxgroup.createAddToCart_1_0_0({}),
  }
}

// `getPropertyRateItemContext` defaults its primary price to `rate.luxPlusTotals?.displayPrice`
// whenever the rate carries member pricing - which the API returns to non-members too, so the
// default would report member prices to the whole non-member population. Resolve the primary on
// redemption eligibility instead, matching the pre-HP-2730 checkout item context
// (`getHotelCheckoutItem`, gated on `getHasLuxPlusOrSubscriptionInCart`). Both prices still ride
// along in `metadata`, so reporting can pick either basis.
function getPropertyAddToCartPrices({ rate, isLuxPlusMember, displayMemberPrice, displayNonMemberPrice }: {
  rate: App.EnrichedPropertyRate
  isLuxPlusMember: boolean
  displayMemberPrice?: number
  displayNonMemberPrice?: number
}) {
  const nonMemberPrice = displayNonMemberPrice ?? rate.totals.displayPrice
  const memberPrice = displayMemberPrice ?? rate.luxPlusTotals?.displayPrice ?? nonMemberPrice
  return {
    displayPrice: isLuxPlusMember ? memberPrice : nonMemberPrice,
    memberPrice,
    nonMemberPrice,
  }
}

function getAddToCartItemContext(contextData: AddToCartContextData): Array<SelfDescribingJson> {
  if (contextData.type === 'bedbank') {
    return getBedbankRateItemContext(
      contextData.offer,
      contextData.rate,
      contextData.price,
      contextData.value,
      contextData.checkIn,
      contextData.checkOut,
      contextData.occupancy,
      contextData.currency,
    )
  }
  const { displayPrice, memberPrice, nonMemberPrice } = getPropertyAddToCartPrices(contextData)
  return getPropertyRateItemContext(
    contextData.occupancy,
    contextData.checkIn,
    contextData.checkOut,
    contextData.rate,
    displayPrice,
    memberPrice,
    nonMemberPrice,
    contextData.displayValue,
  )
}

/**
 * Builds Snowplow item contexts internally (via getBedbankRateItemContext or
 * getPropertyRateItemContext for the item entity, then getRateListContext and
 * getBedbankRateContext/getPropertyRateContext) for a single rate.
 */
export function addToCart(
  contextData?: AddToCartContextData,
): SnowplowEvent | undefined {
  try {
    if (!contextData) {
      return buildAddToCartEvent([])
    }
    const rateContexts = [
      ...getRateListContext(
        contextData.type,
        contextData.ratePosition,
        contextData.type === 'property' ? contextData.rate.orderReason : undefined,
        contextData.rateListId,
        contextData.roomPosition,
        contextData.roomListId,
      ),
      ...(contextData.type === 'bedbank' ? getBedbankRateContext(contextData.rate, contextData.isFlightBundle) : []),
      ...(contextData.type === 'property' ? getPropertyRateContext(contextData.rate) : []),
    ]
    if (!contextData.staysOnPage) {
      return buildAddToCartEvent(rateContexts)
    }
    return buildAddToCartEventWithContext([...getAddToCartItemContext(contextData), ...rateContexts])
  } catch(e) {
    reportClientError(e)
  }
}

export function addPropertyRatesToCart(
  selections: Array<{ rate: App.EnrichedPropertyRate, occupancy: Array<App.Occupants> }>,
  period: { checkIn: string, checkOut: string, isLuxPlusMember: boolean, staysOnPage: boolean },
): SnowplowEvent | undefined {
  try {
    if (!period.staysOnPage) {
      return buildAddToCartEvent(selections.flatMap(({ rate }) => getPropertyRateContext(rate)))
    }
    return buildAddToCartEventWithContext(selections.flatMap(({ rate, occupancy }) => {
      const { displayPrice, memberPrice, nonMemberPrice } = getPropertyAddToCartPrices({
        rate,
        isLuxPlusMember: period.isLuxPlusMember,
      })
      return [
        ...getPropertyRateItemContext(
          occupancy,
          period.checkIn,
          period.checkOut,
          rate,
          displayPrice,
          memberPrice,
          nonMemberPrice,
        ),
        ...getPropertyRateContext(rate),
      ]
    }))
  } catch(e) {
    reportClientError(e)
  }
}

export function hotelItemContextByPackageView(item: App.Checkout.AnyItem, packageView?: App.OfferPackageOptionView, isMultiCart?: boolean) {
  try {
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const state = _store.getState()
    if (isLEHotelItem(item) && packageView) {
      return getHotelCartItem(state, item, packageView, isMultiCart)
    }
  } catch(e) {
    reportClientError(e)
  }
}

export function createMerchandiseItemContextByMerchandiseItem(item: App.Checkout.MerchandiseItem) {
  try {
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const state = _store.getState()
    return getMerchandiseCheckoutItem(state, item)
  } catch(e) {
    reportClientError(e)
  }
}

export async function addToCartMultiItemCheckout(
  isMultiCart?: boolean,
): Promise<SnowplowEvent | undefined> {
  try {
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const state = _store.getState()
    const itemContexts = await getItemsContextForCheckout(state, [], isMultiCart)
    return {
      options: {
        contextsToSkip: ['checkout', 'item', 'luxPlus', 'order'],
      },
      context: [
        ...itemContexts,
      ],
      event: comluxgroup.createAddToCart_1_0_0({}),
    }
  } catch(e) {
    reportClientError(e)
  }
}

export function addToCartWithContext(
  context: Array<SelfDescribingJson>,
): SnowplowEvent {
  return {
    options: {
      contextsToSkip: ['checkout', 'item', 'luxPlus', 'order'],
    },
    context,
    event: comluxgroup.createAddToCart_1_0_0({}),
  }
}

export async function addToCartMultiItem(
  items: Array<App.Checkout.AnyItem>,
  options?: {
    isMultiCart?: boolean
    bedbankContextData?: {
      ratePosition: number
      rateListId: string
      roomPosition: number
      roomListId: string
      rate: App.BedbankRate
      isFlightBundle: boolean
    }
    journeys?: Array<App.JourneyV2>
  },
): Promise<SnowplowEvent | undefined> {
  try {
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const state = _store.getState()
    const { bedbankContextData, isMultiCart, journeys } = options || {}
    const bedbankItemContexts: Array<SelfDescribingJson> = []
    if (bedbankContextData?.rate) {
      const contexts = items
        .filter(isBedbankItem)
        .map(item => getBedbankHotelCartItem(state, item, isMultiCart, bedbankContextData?.rate))
      bedbankItemContexts.push(...nonNullable(contexts))
    }
    const itemContexts = await getItemsContextForCart(state, items, bedbankItemContexts.length ? ['hotel'] : [], isMultiCart)
    const bedbankContext = bedbankContextData ? [
      ...getRateListContext(
        'bedbank',
        bedbankContextData.ratePosition,
        undefined,
        bedbankContextData.rateListId,
        bedbankContextData.roomPosition,
        bedbankContextData.roomListId,
      ),
      ...getBedbankRateContext(bedbankContextData.rate, bedbankContextData.isFlightBundle),
    ] : []

    const flightContexts = getFlightContextsForCheckoutItems(items, state, journeys)

    return {
      options: {
        contextsToSkip: ['checkout', 'item', 'luxPlus', 'order', 'cart'],
      },
      context: [
        ...bedbankContext,
        ...itemContexts,
        ...bedbankItemContexts,
        ...flightContexts,
      ],
      event: comluxgroup.createAddToCart_1_0_0({}),
    }
  } catch(e) {
    reportClientError(e)
  }
}

interface PromotionDisplayCheckContext {
  checkPassed: boolean
  resultCode: string
  promoDisplayConfigId: string
  displayOptionKey: string
}

interface VoucherContext {
  voucherId: string
  voucherName: string
}

export function promotionViewEvent({ promotionContext, promotionCodeContext, promotionDisplayCheckContext, voucherContexts }: {
  promotionCodeContext?: PromotionCodeContext
  promotionContext?: PromotionContext
  promotionDisplayCheckContext?: PromotionDisplayCheckContext
  voucherContexts?: Array<VoucherContext>
}): SnowplowEvent {
  const promotionContextData = promotionContext ? comluxgroup.createPromotion_1_0_0({
    type: promotionContext.promotionType,
    name: promotionContext.promotionName,
  }) : undefined
  const promoCodeContextData = promotionCodeContext ? comluxgroup.createPromotionCode_1_0_0({
    codeName: promotionCodeContext.codeName,
  }) : undefined
  const promotionDisplayCheckData = promotionDisplayCheckContext ? comluxgroup.createPromotionDisplayCheck_1_0_0({
    checkPassed: promotionDisplayCheckContext.checkPassed,
    resultCode: promotionDisplayCheckContext.resultCode,
    promoDisplayConfigId: promotionDisplayCheckContext.promoDisplayConfigId,
    displayOptionKey: promotionDisplayCheckContext.displayOptionKey,
  }) : undefined
  const context: Array<SelfDescribingJson> = []
  if (promotionContextData) {
    context.push(promotionContextData)
  }
  if (promoCodeContextData) {
    context.push(promoCodeContextData)
  }
  if (promotionDisplayCheckData) {
    context.push(promotionDisplayCheckData)
  }
  voucherContexts?.forEach((voucher) => {
    context.push(comluxgroup.createVoucher_1_0_0({
      voucherId: voucher.voucherId,
      voucherName: voucher.voucherName,
    }))
  })
  return {
    context,
    event: comluxgroup.createPromotionView_1_0_0({}),
  }
}

export function promotionClickEvent({ promotionContext, promotionCodeContext, voucherContexts, clickAction }: {
  promotionCodeContext?: PromotionCodeContext
  promotionContext?: PromotionContext
  voucherContexts?: Array<VoucherContext>
  clickAction?: string
}): SnowplowEvent {
  const promotionContextData = promotionContext ? comluxgroup.createPromotion_1_0_0({
    type: promotionContext.promotionType,
    name: promotionContext.promotionName,
  }) : undefined
  const promoCodeContextData = promotionCodeContext ? comluxgroup.createPromotionCode_1_0_0({
    codeName: promotionCodeContext.codeName,
  }) : undefined
  const context: Array<SelfDescribingJson> = []
  if (promotionContextData) {
    context.push(promotionContextData)
  }
  if (promoCodeContextData) {
    context.push(promoCodeContextData)
  }
  voucherContexts?.forEach((voucher) => {
    context.push(comluxgroup.createVoucher_1_0_0({
      voucherId: voucher.voucherId,
      voucherName: voucher.voucherName,
    }))
  })
  return {
    context,
    event: comluxgroup.createPromotionClick_1_0_1({ clickAction }),
  }
}

export function createBookingCancellationEvent(data: {
  orderId: string
  itemId: string
  windowType: string
  action: string
  roomIds?: Array<string>
  decision?: string
  decisionInput?: string
  result?: 'succeeded' | 'failed'
  errorCode?: string
  errorMessage?: string
  isCancellationBlocked?: boolean
  cancellationBlockedReason?: string
}): SelfDescribingJson {
  return comluxgroup.createBookingCancellation_1_0_0({
    ...data,
  })
}

export function getItemContextForOffer(offer: App.AnyOffer) {
  const state = _store?.getState()

  if (!state) {
    return
  }

  return getAnyItemContextForOffer({ state, offer })
}

export function activateCampaignEvent(campaignId: string, experimentId: string, variationId: string): SnowplowEvent {
  return {
    event: comluxgroup.createActivateAbTest_1_0_0({
      campaignId,
      experimentId,
      variationId,
    }),
  }
}

export type SnowplowDestinationContext = {
  destinationId: string
  destinationName: string
  tileType: string
  destinationImageId?: string
}

export function destinationImpressionEventWithContext(
  position: number,
  listName: string,
  listInstance: string,
  destination: SnowplowDestinationContext,
  filter?: string,
): SnowplowEvent {
  return {
    context: [
      comluxgroup.createListItem_1_0_0({
        listInstance,
        listName: filter ? `${listName}-${filter}` : listName,
        itemPosition: position,
      }),
      comluxgroup.createDestination_1_0_0({
        destinationId: destination.destinationId,
        destinationName: destination.destinationName,
        tileType: destination.tileType,
        destinationImageId: destination.destinationImageId,
      }),
    ],
    event: comluxgroup.createDestinationImpression_1_0_0({}),
  }
}

export function destinationClickEventWithContext(
  position: number,
  listName: string,
  listInstance: string,
  destination: SnowplowDestinationContext,
  filter?: string,
): SnowplowEvent {
  return {
    context: [
      comluxgroup.createListItem_1_0_0({
        listInstance,
        listName: filter ? `${listName}-${filter}` : listName,
        itemPosition: position,
      }),
      comluxgroup.createDestination_1_0_0({
        destinationId: destination.destinationId,
        destinationName: destination.destinationName,
        tileType: destination.tileType,
        destinationImageId: destination.destinationImageId,
      }),
    ],
    event: comluxgroup.createDestinationClick_1_0_0({}),
  }
}

export function getUpgradeItemContext(
  originalPackage: App.OfferPackageOptionView,
  offeredPackage: App.OfferPackageOptionView,
  item: App.Checkout.InstantBookingLEHotelItem,
): Array<SelfDescribingJson> | undefined {
  try {
    const state = _store?.getState()
    if (!state) { throw new Error('No redux registered for tracking purchase success snowplow events') }
    const choseOfferedUpgrade = offeredPackage.package.id === item.packageId && offeredPackage.package.roomRate?.id === item.roomRateId
    const luxPlusMemberPriceApplies = getHasLuxPlusOrSubscriptionInCart(state)
    const originalPrice = luxPlusMemberPriceApplies ? originalPackage.hotelMemberTotal : originalPackage.hotelTotal
    const offeredPrice = luxPlusMemberPriceApplies ? offeredPackage.hotelMemberTotal : offeredPackage.hotelTotal
    const originalItem = getHotelCheckoutItem(
      state,
      item,
      {
        itemId: choseOfferedUpgrade ? null : item.itemId,
        packageId: originalPackage.package.id,
        typeId: originalPackage.package.roomType?.id,
        rateId: originalPackage.package.roomRate?.id,
        price: originalPrice,
        value: originalPackage.hotelValue,
      },
      {
        upgradeOption: 'original',
        memberPrice: originalPackage.hotelMemberTotal > 0 ? originalPackage.hotelMemberTotal : originalPackage.hotelTotal,
        nonMemberPrice: originalPackage.hotelTotal,
      },
    )
    const offeredItem = getHotelCheckoutItem(
      state,
      item,
      {
        itemId: choseOfferedUpgrade ? item.itemId : null,
        packageId: offeredPackage.package.id,
        typeId: offeredPackage.package.roomType?.id,
        rateId: offeredPackage.package.roomRate?.id,
        price: offeredPrice,
        value: offeredPackage.hotelValue,
      },
      {
        upgradeOption: 'offered',
        memberPrice: offeredPackage.hotelMemberTotal > 0 ? offeredPackage.hotelMemberTotal : offeredPackage.hotelTotal,
        nonMemberPrice: offeredPackage.hotelTotal,
      },
    )
    return [
      originalItem,
      offeredItem,
    ]
  } catch(e) {
    reportClientError(e)
  }
}

export function upgradeEvent(
  upgradeData: {
    chosenCartItemId: string
    hasUpgraded: boolean
  },
  upgradeItemContext: Array<SelfDescribingJson> | undefined,
  contextTypes: Array<SnowplowItemContextType>,
): SnowplowEvent | undefined {
  try {
    return {
      options: {
        itemsToSkip: contextTypes,
      },
      context: upgradeItemContext,
      event: comluxgroup.createUpgrade_1_0_0({
        cartItemId: upgradeData.chosenCartItemId,
        upgraded: upgradeData.hasUpgraded,
      }),
    }
  } catch(e) {
    reportClientError(e)
  }
}

export async function multiCartCheckoutNowEvent(activeItems: Array<App.Checkout.AnyItem>) {
  try {
    const state = _store?.getState()
    if (!state) return

    const context = await getItemsContextForCart(state, activeItems, [], true)
    trackClientEvent({
      subject: 'checkout_now',
      action: 'click',
      category: 'multi_cart',
      type: 'interaction',
      context,
      options: {
        ...cartEventBaseOptions,
      },
    })
  } catch(e) {
    reportClientError(e, { message: 'Failed to track multi cart checkout now event' })
  }
}

export async function multiCartSaveAllForLaterEvent(items: Array<App.Checkout.AnyItem>) {
  try {
    const state = _store?.getState()
    if (!state) return

    const context = await getItemsContextForCart(state, items, [], true)
    trackClientEvent({
      subject: 'save_all_for_later',
      action: 'click',
      category: 'multi_cart',
      type: 'interaction',
      context,
      options: {
        ...cartEventBaseOptions,
      },
    })
  } catch(e) {
    reportClientError(e, { message: 'Failed to track multi cart save all for later event' })
  }
}

export async function multiCartDeleteAllEvent(items: Array<App.Checkout.AnyItem>) {
  try {
    const state = _store?.getState()
    if (!state) return

    const context = await getItemsContextForCart(state, items, [], true)
    trackClientEvent({
      subject: 'delete_all',
      action: 'click',
      category: 'multi_cart',
      type: 'interaction',
      context,
      options: {
        ...cartEventBaseOptions,
      },
    })
  } catch(e) {
    reportClientError(e, { message: 'Failed to track multi cart delete all event' })
  }
}

export async function multiCartSaveMultipleItemsForLaterEvent(items: Array<App.Checkout.AnyItem>, isSavedForLater: boolean) {
  try {
    const state = _store?.getState()
    if (!state) return

    const context = await getItemsContextForCart(state, items, [], true)
    trackClientEvent({
      subject: isSavedForLater ? 'add_item' : 'save_for_later_item',
      action: 'click',
      category: 'multi_cart',
      type: 'interaction',
      context,
      options: {
        ...cartEventBaseOptions,
      },
    })
  } catch(e) {
    reportClientError(e, { message: 'Failed to track multi cart save item for later event' })
  }
}

/**
* @deprecated simply call multiCartSaveMultipleItemsForLaterEvent instead
*/
export async function multiCartSaveItemForLaterEvent(item: App.Checkout.AnyItem, isSavedForLater: boolean) {
  try {
    const state = _store?.getState()
    if (!state) return

    const context = await getItemsContextForCart(state, [item], [], true)
    trackClientEvent({
      subject: isSavedForLater ? 'add_item' : 'save_for_later_item',
      action: 'click',
      category: 'multi_cart',
      type: 'interaction',
      context,
      options: {
        ...cartEventBaseOptions,
      },
    })
  } catch(e) {
    reportClientError(e, { message: 'Failed to track multi cart save item for later event' })
  }
}

type DoubleBookingWarningAction = 'review cart' | 'continue to checkout' | 'impression'

export async function doubleBookingWarningEvents(
  itemIds: Array<string>,
  status: DoubleBookingWarningAction,
) {
  try {
    const state = _store?.getState()
    if (!state) return

    const cartMachineActor = getCartMachineActor()
    if (!cartMachineActor) return
    const snapshot = cartMachineActor.getSnapshot()
    const availableItems = getAvailableItems(snapshot)
    const items = availableItems.filter(item => itemIds.includes(item.itemId))

    const context = await getItemsContextForCart(state, items, [], true)

    trackClientEvent({
      subject: 'overlapping_booking_warning',
      action: status,
      category: 'multi_cart',
      type: status === 'impression' ? 'nonInteraction' : 'interaction',
      options: {
        ...cartEventBaseOptions,
      },
      context,
    })
  } catch(e) {
    reportClientError(e, {
      message: 'Failed to track overlapping booking warning events',
    })
  }
}

export async function multiCartCreateCartEvent() {
  trackClientEvent({
    subject: 'create_new_cart',
    action: 'click',
    category: 'multi_cart',
    type: 'interaction',
    options: {
      ...cartEventBaseOptions,
    },
  })
}

export async function multiCartDeleteItemEvent(item: App.Checkout.AnyItem) {
  try {
    const state = _store?.getState()
    if (!state) return

    const context = await getItemsContextForCart(state, [item], [], true)
    trackClientEvent({
      subject: 'delete_item',
      action: 'click',
      category: 'multi_cart',
      type: 'interaction',
      context,
      options: {
        ...cartEventBaseOptions,
      },
    })
  } catch(e) {
    reportClientError(e, { message: 'Failed to track multi cart delete item event' })
  }
}

export async function multiCartRefreshFlightItemEvent(item: App.Checkout.FlightItem, status?: 'success' | 'failed') {
  try {
    const state = _store?.getState()
    if (!state) return
    const context = await getItemsContextForCart(state, [item], [], true)
    const action = status === 'success' ? 'returned' : 'not_returned'

    trackClientEvent({
      subject: 'refresh_flight_search',
      action: status ? action : 'click',
      category: 'multi_cart',
      type: 'interaction',
      context,
      options: {
        ...cartEventBaseOptions,
      },
    })
  } catch(e) {
    reportClientError(e, { message: 'Failed to track multi cart refresh flight item event' })
  }
}

export function cartCommentNewEvent(cartName: string) {
  trackClientEvent({
    subject: 'new_comment',
    action: cartName,
    category: 'cart_comment',
    type: 'interaction',
    options: {
      ...cartEventBaseOptions,
    },
  })
}

export function cartCommentEditEvent(cartName: string) {
  trackClientEvent({
    subject: 'edit_comment',
    action: cartName,
    category: 'cart_comment',
    type: 'interaction',
    options: {
      ...cartEventBaseOptions,
    },
  })
}

export function cartCommentDeleteEvent(cartName: string) {
  trackClientEvent({
    subject: 'delete_comment',
    action: cartName,
    category: 'cart_comment',
    type: 'interaction',
    options: {
      ...cartEventBaseOptions,
    },
  })
}

interface DynamicBannerEventParams {
  bannerTitle: string
}

export function dynamicBannerImpressionEvent(params: DynamicBannerEventParams): SnowplowEvent {
  return {
    context: [
      comluxgroup.createPromotion_1_0_0({
        type: 'banner',
        name: params.bannerTitle,
      }),
    ],
    event: comluxgroup.createPromotionView_1_0_0({}),
  }
}

export function dynamicBannerClickEvent(params: DynamicBannerEventParams): SnowplowEvent {
  return {
    context: [
      comluxgroup.createPromotion_1_0_0({
        type: 'banner',
        name: params.bannerTitle,
      }),
    ],
    event: comluxgroup.createPromotionClick_1_0_0({}),
  }
}

export function promoDisplayCheckEvent(data: App.PromoDisplayConfigResult): SnowplowEvent {
  return {
    title: 'promo_display_check',
    context: [
      comluxgroup.createPromotionDisplayCheck_1_0_0({
        checkPassed: data.checkPassed,
        resultCode: data.resultCode,
        promoDisplayConfigId: data.promoDisplayConfigId,
        displayOptionKey: data.displayOptionKey,
      }),
    ],
  }
}

export type TypeaheadActionType = 'select' | 'default'
export type TypeaheadSubjectType = 'popular' | 'search_results'

export function nlsTypeaheadSuggestedClicked(subject: TypeaheadSubjectType, actionName: TypeaheadActionType): void {
  trackClientEvent({
    subject,
    action: actionName,
    category: 'typeahead',
    type: 'interaction',
    optimizelyEventId: '5870764570705920',
    optimizelyEventKey: 'natural_language_typeahead_suggested_clicked',
  })
}
